import { HttpRequestError, requestErrorDetails } from '../shared/request-error.js';
import { isSatelliteBasemap } from '../shared/satellite-access.js';
import type { MapAnalyticsTrack } from '../shared/analytics-schema.js';

const THUMB_MAX = 480;

type ThumbnailTrack = MapAnalyticsTrack;

export type ThumbnailUploadSource =
  | 'save_button'
  | 'share_autosave'
  | 'missing_thumbnail_repair'
  | 'post_signin_draft_autosave';

interface ThumbnailUploadOptions {
  images?: { thumbnail: Blob; share: Blob };
  source?: ThumbnailUploadSource;
  track?: ThumbnailTrack;
}

interface ThumbnailDiagnostics {
  has_viewport: string;
  has_map_canvas: string;
  has_drawing_canvas: string;
  ol_canvas_count: string;
  viewport_width: string;
  viewport_height: string;
  drawing_count: string;
}

function thumbnailDiagnostics(state: any): ThumbnailDiagnostics {
  const viewport = state.mapViewportElement ?? null;
  const rect = viewport?.getBoundingClientRect?.();
  const olCanvasCount = viewport?.querySelectorAll?.('canvas')?.length ?? 0;
  return {
    has_viewport: String(!!viewport),
    has_map_canvas: String(olCanvasCount > 0),
    has_drawing_canvas: String(!!state.canvas),
    ol_canvas_count: String(Math.min(Number(olCanvasCount) || 0, 99)),
    viewport_width: String(Math.max(0, Math.round(rect?.width ?? 0))),
    viewport_height: String(Math.max(0, Math.round(rect?.height ?? 0))),
    drawing_count: String(Math.min(Array.isArray(state.strokes) ? state.strokes.length : 0, 9999)),
  };
}

function thumbnailSkipReason(diagnostics: ThumbnailDiagnostics): string {
  if (diagnostics.has_viewport !== 'true') return 'no_viewport';
  if (diagnostics.has_map_canvas !== 'true') return 'no_map_canvas';
  if (diagnostics.has_drawing_canvas !== 'true') return 'no_drawing_canvas';
  if (diagnostics.viewport_width === '0' || diagnostics.viewport_height === '0') return 'empty_viewport';
  return 'to_blob_null';
}

function errorBlob(cause: unknown): string {
  if (cause instanceof Error) {
    return JSON.stringify({
      name: cause.name,
      message: cause.message,
      stack_head: cause.stack?.split('\n').slice(0, 3).join(' | ')
    }).slice(0, 4096);
  }
  return JSON.stringify({ message: String(cause) }).slice(0, 4096);
}

function captureMapCanvas(state: any): HTMLCanvasElement | null {
  // Force OL to finish any in-progress render so canvas pixels are current.
  state.olMap?.renderSync?.();

  const olCanvases = Array.from<HTMLCanvasElement>(
    state.mapViewportElement?.querySelectorAll('canvas') ?? []
  );
  if (!olCanvases.length || !state.canvas) return null;

  const dpr = window.devicePixelRatio || 1;
  const vpRect = state.mapViewportElement.getBoundingClientRect();
  const srcW = Math.round(vpRect.width * dpr);
  const srcH = Math.round(vpRect.height * dpr);
  if (!srcW || !srcH) return null;

  const full = document.createElement('canvas');
  full.width = srcW;
  full.height = srcH;
  const fctx = full.getContext('2d', { willReadFrequently: true })!;

  fctx.fillStyle = '#e8eef1';
  fctx.fillRect(0, 0, srcW, srcH);
  for (const olCanvas of isSatelliteBasemap(state.currentBasemap) ? [] : olCanvases) {
    fctx.globalAlpha = olCanvas.parentElement?.style?.opacity === ''
      ? 1
      : Number(olCanvas.parentElement?.style?.opacity ?? 1);
    // OL's CSS transform maps canvas pixels → CSS viewport pixels (includes 1/dpr scale).
    // For compositing into a device-pixel canvas we need canvas pixels → device pixels,
    // so multiply every matrix component by dpr to undo the display-only scale.
    const t = olCanvas.style.transform;
    if (t) {
      const m = t.match(/^matrix\(([^()]*)\)$/);
      if (m) {
        const [a, b, c, d, e, f] = m[1].split(',').map(Number);
        fctx.setTransform(a * dpr, b * dpr, c * dpr, d * dpr, e * dpr, f * dpr);
      }
    }
    fctx.drawImage(olCanvas, 0, 0);
    fctx.setTransform(1, 0, 0, 1, 0, 0);
    fctx.globalAlpha = 1;
  }
  fctx.drawImage(state.canvas, 0, 0);

  return full;
}

export async function captureThumbnailBlobs(state: any): Promise<{ thumbnail: Blob; share: Blob } | null> {
  const full = captureMapCanvas(state);
  if (!full) return null;
  const srcW = full.width;
  const srcH = full.height;
  const scale = Math.min(THUMB_MAX / srcW, THUMB_MAX / srcH, 1);
  const tw = Math.round(srcW * scale);
  const th = Math.round(srcH * scale);
  const thumb = document.createElement('canvas');
  thumb.width = tw;
  thumb.height = th;
  thumb.getContext('2d', { willReadFrequently: true })!.drawImage(full, 0, 0, tw, th);

  const share = document.createElement('canvas');
  share.width = 1200;
  share.height = 630;
  const shareContext = share.getContext('2d')!;
  shareContext.fillStyle = '#e8eef1';
  shareContext.fillRect(0, 0, share.width, share.height);
  const shareScale = Math.min(share.width / srcW, share.height / srcH);
  const width = Math.round(srcW * shareScale);
  const height = Math.round(srcH * shareScale);
  shareContext.drawImage(full, (share.width - width) / 2, (share.height - height) / 2, width, height);

  const thumbnail = await new Promise<Blob | null>((resolve) => thumb.toBlob(resolve, 'image/webp', 0.85));
  let socialImage: Blob | null = null;
  for (const quality of [0.85, 0.7, 0.5]) {
    socialImage = await new Promise<Blob | null>((resolve) => share.toBlob(resolve, 'image/jpeg', quality));
    if (socialImage && socialImage.size <= 512 * 1024) break;
  }
  if (!thumbnail || !socialImage || socialImage.size > 512 * 1024) return null;
  return { thumbnail, share: socialImage };
}

export async function uploadThumbnail(state: any, mapId: string, options: ThumbnailUploadOptions = {}): Promise<void> {
  const source = options.source ?? 'save_button';
  const track = options.track;
  const diagnostics = thumbnailDiagnostics(state);
  const baseProps = { source, ...diagnostics };

  track?.('thumbnail_upload_attempted', baseProps);

  let images: { thumbnail: Blob; share: Blob } | null = null;
  try {
    images = options.images ?? await captureThumbnailBlobs(state);
  } catch (error) {
    track?.('thumbnail_capture_failed', {
      ...baseProps,
      reason: 'capture_exception',
      error_blob: errorBlob(error)
    });
    throw error;
  }

  if (!images) {
    const reason = thumbnailSkipReason(diagnostics);
    track?.('thumbnail_capture_skipped', {
      ...baseProps,
      reason,
      error_blob: JSON.stringify({ message: `Thumbnail capture returned no image: ${reason}` })
    });
    throw new Error(`Thumbnail capture returned no image: ${reason}`);
  }

  for (const [variant, blob] of Object.entries(images)) {
    const uploadProps = { ...baseProps, blob_size: String(blob.size) };
    let res: Response;
    try {
      res = await fetch(`/api/maps/${encodeURIComponent(mapId)}/thumbnail${variant === 'share' ? '?variant=share' : ''}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'Content-Type': blob.type, 'X-Thumbnail-Policy': 'no-satellite-imagery' },
        body: blob,
      });
    } catch (error) {
      track?.('thumbnail_upload_failed', {
        ...uploadProps,
        reason: 'network_error',
        error_blob: errorBlob(error)
      });
      throw error;
    }

    if (!res.ok) {
      const error = new HttpRequestError(`thumbnail PUT ${res.status}`, res, 'thumbnail.upload');
      track?.('thumbnail_upload_failed', {
        ...uploadProps,
        reason: 'http_error',
        status: String(res.status),
        error_blob: JSON.stringify(requestErrorDetails(error))
      });
      throw error;
    }
  }
  track?.('thumbnail_upload_succeeded', { ...baseProps, blob_size: String(images.thumbnail.size + images.share.size) });
}
